import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, Share2, Type, Minus, Plus, Eye, EyeOff } from "lucide-react";
import { FaSpotify, FaYoutube, FaApple } from "react-icons/fa";
import { http } from "@/lib/api";
import SEO from "@/components/SEO";
import { Section } from "@/components/Section";
import Artwork from "@/components/Artwork";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useMusic } from "@/context/MusicPlayer";
import { TID } from "@/constants/testIds";

const LyricDetail = () => {
  const { id } = useParams();
  const [l, setL] = useState(null);
  const [related, setRelated] = useState(null);
  const [fontSize, setFontSize] = useState(24);
  const [reader, setReader] = useState(false);
  const { play } = useMusic();

  useEffect(() => {
    setL(null);
    http.get(`/lyrics/${id}`).then((r) => setL(r.data)).catch(() => setL(null));
  }, [id]);

  useEffect(() => {
    if (!l) return;
    http.get("/releases").then((r) => {
      const match = r.data.find((x) => x.title === l.title || x.title.startsWith(l.title.split(" (")[0]));
      setRelated(match || null);
    });
  }, [l]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch (_) {
      toast.error("Copy failed");
    }
  };

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: `${l.title} — KASHiX Lyrics`, url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch (_e) { /* share cancelled */ }
  };

  const jsonLd = useMemo(() => {
    if (!l) return null;
    return {
      "@context": "https://schema.org",
      "@type": "CreativeWork",
      name: `${l.title} — Lyrics & Meaning`,
      alternateName: l.hindi_title,
      about: l.deity,
      inLanguage: ["sa", "hi", "en"],
      author: { "@type": "MusicGroup", name: "KASHiX" },
    };
  }, [l]);

  if (!l) return <div className="min-h-screen flex items-center justify-center text-white/40">Loading…</div>;

  return (
    <>
      <SEO
        title={`${l.title} — Lyrics & Meaning`}
        description={`${l.title} (${l.hindi_title}) — Sanskrit lyrics, Hindi meaning, English translation. Devoted to ${l.deity}.`}
        image={related?.cover_image}
        jsonLd={jsonLd}
      />

      {/* HERO */}
      <section className="relative pt-32 pb-12 overflow-hidden">
        {related && (
          <div className="absolute inset-0" style={{ backgroundImage: `url(${related.cover_image})`, backgroundSize: "cover", filter: "blur(60px)", opacity: 0.3 }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/80 to-black" />
        <div className="relative mx-auto max-w-[1000px] px-6 md:px-10">
          <Link to="/lyrics" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-white/60 hover:text-[#FF9933]">
            <ArrowLeft size={14} /> Divine Library
          </Link>
          <div className="mt-6 flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933] bg-[#FF9933]/10 border border-[#FF9933]/30 rounded-full px-3 py-1">
              {l.deity}
            </span>
            {l.duration && <span className="text-[11px] text-white/50">{l.duration}</span>}
          </div>
          <div className="mt-4 font-hindi text-4xl md:text-5xl text-[#FF9933]">{l.hindi_title}</div>
          <h1 className="mt-2 font-display text-5xl md:text-7xl tracking-tight text-white">{l.title}</h1>
          <div className="mt-8 divider-saffron" />
        </div>
      </section>

      {/* SPOTIFY EMBED */}
      {related && (
        <Section className="!py-6">
          <div className="max-w-[1000px] mx-auto glass rounded-2xl overflow-hidden border border-[#FF9933]/20">
            <iframe
              title={`Spotify — ${l.title}`}
              src={related.spotify_embed_url || "https://open.spotify.com/embed/artist/3CVKryef8hUWE97XMj2cw1?theme=0"}
              width="100%"
              height="152"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
              className="block bg-transparent"
            />
          </div>
        </Section>
      )}

      {/* TOOLBAR */}
      <Section className="!py-6">
        <div className="max-w-[1000px] mx-auto flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 glass rounded-full px-3 py-2">
            <Type size={14} className="text-white/60" />
            <button onClick={() => setFontSize((s) => Math.max(16, s - 2))} aria-label="Decrease font" className="p-1 hover:text-[#FF9933]">
              <Minus size={14} />
            </button>
            <span className="text-[11px] text-white/60">{fontSize}px</span>
            <button onClick={() => setFontSize((s) => Math.min(48, s + 2))} aria-label="Increase font" className="p-1 hover:text-[#FF9933]">
              <Plus size={14} />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setReader((v) => !v)} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-[11px] uppercase tracking-[0.25em] hover:text-[#FF9933] hover:border-[#FF9933] transition">
              {reader ? <EyeOff size={12} /> : <Eye size={12} />} {reader ? "Exit Reading Mode" : "Reading Mode"}
            </button>
            <button onClick={share} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 text-[11px] uppercase tracking-[0.25em] hover:text-[#FF9933] hover:border-[#FF9933] transition">
              <Share2 size={12} /> Share
            </button>
          </div>
        </div>
      </Section>

      {/* LYRIC BODY */}
      <Section className="!py-8">
        <div className={`mx-auto ${reader ? "max-w-[820px]" : "max-w-[1000px]"}`}>
          <Tabs defaultValue="sanskrit" className="w-full">
            <TabsList className="bg-transparent border border-white/10 rounded-full p-1 gap-1 mx-auto flex w-fit">
              <TabsTrigger data-testid={TID.lyricReaderTabSanskrit} value="sanskrit" className="rounded-full px-5 text-xs uppercase tracking-[0.25em] data-[state=active]:bg-[#FF9933] data-[state=active]:text-black">Sanskrit</TabsTrigger>
              <TabsTrigger data-testid={TID.lyricReaderTabHindi} value="hindi" className="rounded-full px-5 text-xs uppercase tracking-[0.25em] data-[state=active]:bg-[#FF9933] data-[state=active]:text-black">Hindi Meaning</TabsTrigger>
              <TabsTrigger data-testid={TID.lyricReaderTabEnglish} value="english" className="rounded-full px-5 text-xs uppercase tracking-[0.25em] data-[state=active]:bg-[#FF9933] data-[state=active]:text-black">English</TabsTrigger>
            </TabsList>

            <TabsContent value="sanskrit" className="relative">
              <button onClick={() => copy(l.sanskrit, "Sanskrit")} className="absolute right-0 -top-2 md:top-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-[#FF9933]">
                <Copy size={11} /> Copy
              </button>
              <pre className="mt-10 whitespace-pre-wrap font-hindi leading-loose text-white text-center" style={{ fontSize }}>{l.sanskrit}</pre>
            </TabsContent>
            <TabsContent value="hindi" className="relative">
              <button onClick={() => copy(l.hindi, "Hindi meaning")} className="absolute right-0 -top-2 md:top-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-[#FF9933]">
                <Copy size={11} /> Copy
              </button>
              <p className="mt-10 font-hindi-body leading-loose text-white/85 text-center" style={{ fontSize }}>{l.hindi}</p>
            </TabsContent>
            <TabsContent value="english" className="relative">
              <button onClick={() => copy(l.english_translation, "English translation")} className="absolute right-0 -top-2 md:top-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-[#FF9933]">
                <Copy size={11} /> Copy
              </button>
              <p className="mt-10 font-editorial leading-loose text-white/90 text-center" style={{ fontSize: fontSize + 2 }}>“{l.english_translation}”</p>
            </TabsContent>
          </Tabs>
        </div>
      </Section>

      {/* RELATED */}
      {related && (
        <Section className="!py-14">
          <div className="max-w-[1000px] mx-auto grid gap-6 md:grid-cols-2">
            <div className="glass rounded-2xl p-8">
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">Related Release</div>
              <div className="mt-3 flex gap-4 items-center">
                <div className="w-16 h-16"><Artwork src={related.cover_image} alt="" className="w-full h-full object-cover" rounded="rounded-lg" /></div>
                <div>
                  <div className="font-hindi text-lg text-white/85">{related.hindi_title}</div>
                  <Link to={`/releases/${related.id}`} className="font-display text-xl text-white hover:text-[#FF9933]">{related.title}</Link>
                </div>
              </div>
              <div className="mt-5 flex items-center gap-3">
                <button onClick={() => play(related)} className="inline-flex items-center gap-2 rounded-full bg-[#FF9933] text-black px-4 py-2 text-[10px] uppercase tracking-[0.3em] font-semibold hover:bg-[#FFD700]">Play</button>
                <a href={related.spotify_url} target="_blank" rel="noreferrer noopener" className="text-white/70 hover:text-[#FF9933]"><FaSpotify size={16} /></a>
                <a href={related.apple_music_url} target="_blank" rel="noreferrer noopener" className="text-white/70 hover:text-[#FF9933]"><FaApple size={16} /></a>
                <a href={related.youtube_url} target="_blank" rel="noreferrer noopener" className="text-white/70 hover:text-[#FF9933]"><FaYoutube size={16} /></a>
              </div>
            </div>
            <div className="glass rounded-2xl p-8">
              <div className="text-[10px] uppercase tracking-[0.35em] text-[#FF9933]">Support KASHiX</div>
              <p className="mt-3 text-white/70">Independent devotional music depends on you. Fuel the next chapter.</p>
              <Link to="/support" className="mt-5 inline-flex items-center gap-2 rounded-full border border-white/15 text-white px-4 py-2 text-[10px] uppercase tracking-[0.3em] hover:text-[#FF9933] hover:border-[#FF9933]">
                Support
              </Link>
            </div>
          </div>
        </Section>
      )}
    </>
  );
};

export default LyricDetail;
